import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

const P5Countdown = ({ targetDate, onComplete }) => {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  // 计算剩余时间（使用东八区/北京时间）
  const calculateTimeLeft = () => {
    // 获取当前UTC时间
    const nowUTC = new Date();

    // 计算东八区时间偏移（UTC+8）
    const chinaTimezoneOffset = 8 * 60 * 60 * 1000; // 8小时转毫秒

    // 获取当前UTC时间的毫秒数
    const nowUTCTime = nowUTC.getTime();

    // 获取用户本地时区偏移（相对于UTC，单位为分钟）
    const localTimezoneOffsetMinutes = nowUTC.getTimezoneOffset();
    const localTimezoneOffset = localTimezoneOffsetMinutes * 60 * 1000; // 转换为毫秒

    // 计算当前东八区时间（先转到UTC，再加上东八区偏移）
    const nowChinaTime = nowUTCTime + localTimezoneOffset + chinaTimezoneOffset;

    // 目标日期也需要确保是东八区时间
    // 假设targetDate已经是东八区时间的字符串表示，如 "2025-05-20T21:00:00+08:00"
    const targetDateTime = new Date(targetDate).getTime();

    // 计算时间差
    const distance = targetDateTime - nowChinaTime;

    if (distance <= 0) {
      onComplete && onComplete();
      return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    }

    const days = Math.floor(distance / (1000 * 60 * 60 * 24));
    const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((distance % (1000 * 60)) / 1000);

    return { days, hours, minutes, seconds };
  };

  // 格式化数字为两位数
  const formatNumber = (num) => {
    return num < 10 ? `0${num}` : num;
  };

  // 倒计时逻辑
  useEffect(() => {
    const updateTimer = () => {
      setTimeLeft(calculateTimeLeft());
    };

    // 立即更新一次
    updateTimer();

    // 设置定时器每秒更新一次
    const timer = setInterval(updateTimer, 1000);

    return () => clearInterval(timer);
  }, [targetDate]);

  // 提取目标日期的可读格式（确保显示为东八区/北京时间）
  const targetDateObj = new Date(targetDate);

  // 格式化为中国标准时间显示
  const formattedDate = `${targetDateObj.getFullYear()}年${targetDateObj.getMonth() + 1}月${targetDateObj.getDate()}日 ${targetDateObj.getHours()}:${formatNumber(targetDateObj.getMinutes())} (UTC+8)`;

  // 卡片动画变体
  const cardVariants = {
    initial: { scale: 0.96, opacity: 0 },
    animate: { scale: 1, opacity: 1 },
    hover: { scale: 1.05, boxShadow: '0 0 20px rgba(120, 140, 255, 0.3)' }
  };

  // 数字翻转动画变体
  const numberVariants = {
    initial: { opacity: 0, y: 10 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -10 }
  };

  return (
    <div className="flex flex-col items-center w-full">

      {/* 倒计时卡片容器 */}
      <div className="flex justify-center space-x-4 w-full">
        {/* 天数卡片 */}
        {timeLeft.days > 0 && (
          <motion.div
            className="flex flex-col items-center justify-center w-24 h-28 bg-gradient-to-b from-blue-600/40 to-indigo-700/40 rounded-xl backdrop-blur-sm border border-blue-400/20"
            variants={cardVariants}
            initial="initial"
            animate="animate"
            whileHover="hover"
            transition={{ duration: 0.4 }}
          >
            <motion.span
              className="text-4xl font-bold text-blue-200"
              key={timeLeft.days}
              variants={numberVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{ duration: 0.3 }}
            >
              {formatNumber(timeLeft.days)}
            </motion.span>
            <span className="text-sm text-blue-300 mt-1">天</span>
          </motion.div>
        )}

        {/* 小时卡片 */}
        <motion.div
          className="flex flex-col items-center justify-center w-24 h-28 bg-gradient-to-b from-blue-600/40 to-indigo-700/40 rounded-xl backdrop-blur-sm border border-blue-400/20"
          variants={cardVariants}
          initial="initial"
          animate="animate"
          whileHover="hover"
          transition={{ duration: 0.4, delay: 0.1 }}
        >
          <motion.span
            className="text-4xl font-bold text-blue-200"
            key={timeLeft.hours}
            variants={numberVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.3 }}
          >
            {formatNumber(timeLeft.hours)}
          </motion.span>
          <span className="text-sm text-blue-300 mt-1">时</span>
        </motion.div>

        {/* 分钟卡片 */}
        <motion.div
          className="flex flex-col items-center justify-center w-24 h-28 bg-gradient-to-b from-blue-600/40 to-indigo-700/40 rounded-xl backdrop-blur-sm border border-blue-400/20"
          variants={cardVariants}
          initial="initial"
          animate="animate"
          whileHover="hover"
          transition={{ duration: 0.4, delay: 0.2 }}
        >
          <motion.span
            className="text-4xl font-bold text-blue-200"
            key={timeLeft.minutes}
            variants={numberVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.3 }}
          >
            {formatNumber(timeLeft.minutes)}
          </motion.span>
          <span className="text-sm text-blue-300 mt-1">分</span>
        </motion.div>

        {/* 秒数卡片 */}
        <motion.div
          className="flex flex-col items-center justify-center w-24 h-28 bg-gradient-to-b from-blue-600/40 to-indigo-700/40 rounded-xl backdrop-blur-sm border border-blue-400/20"
          variants={cardVariants}
          initial="initial"
          animate="animate"
          whileHover="hover"
          transition={{ duration: 0.4, delay: 0.3 }}
        >
          <motion.span
            className="text-4xl font-bold text-blue-200"
            key={timeLeft.seconds}
            variants={numberVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.3 }}
          >
            {formatNumber(timeLeft.seconds)}
          </motion.span>
          <span className="text-sm text-blue-300 mt-1">秒</span>
        </motion.div>
      </div>
    </div>
  );
};

export default P5Countdown;
